import { yupResolver } from '@hookform/resolvers/yup';
import CurrencyExchangeOutlined from '@mui/icons-material/CurrencyExchangeOutlined';
import { BaseModal } from '@src/common/components/BaseModal';
import { DatePicker } from '@src/common/components/dates/DatePicker';
import { Divider } from '@src/common/components/Divider';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import {
  CadRateField,
  UsdRateField,
} from '@src/exchangeRateManager/ExchangeRateFields';
import { useSetNextExchangeRate } from '@src/exchangeRateManager/hooks/useSetNextExchangeRate';
import { ControllerFormNumberInput } from '@src/orders/PermitFormSections/ControllerFormNumberInput';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { InferType } from 'yup';
import { object as yupObject } from 'yup';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  titleIcon?: ReactNode;
  marginBottom?: string;
}

const NewExchangeRateSchema = yupObject()
  .shape({
    [UsdRateField.fieldName]: UsdRateField.validator,
    [CadRateField.fieldName]: CadRateField.validator,
  })
  .required();

export const SetExchangeRateModal = (props: ModalProps) => {
  const { t } = useTranslation();
  const { showSuccessToast } = useToast();
  const { setExchangeRate } = useSetNextExchangeRate({
    onSuccess: () => {
      showSuccessToast(t('exchangeRate.new.success'));
      props.onClose();
    },
  });

  const { control } = useForm({
    resolver: yupResolver(NewExchangeRateSchema),
    mode: 'onSubmit',
  });

  const submitForm = (data: InferType<typeof NewExchangeRateSchema>) => {
    setExchangeRate({
      data: {
        cadToUsd: data[CadRateField.fieldName],
        usdToCad: data[UsdRateField.fieldName],
      },
    });
  };

  return (
    <BaseModal
      open={props.open}
      onClose={props.onClose}
      title={props.title}
      titleIcon={<CurrencyExchangeOutlined />}
    >
      <form onSubmit={control.handleSubmit(submitForm)}>
        <Stack direction="column" gap={theme.spacing['4']}>
          <DatePicker
            disabled
            value={new Date()}
            readOnly={true}
            onChange={(_) => {}}
            label={t('exchangeRate.startDate')}
          />
          <ControllerFormNumberInput
            control={control}
            label={t('fields.usdRate')}
            helperText={t('exchangeRate.usdToCad')}
            fieldName={UsdRateField.fieldName}
            step={0.01}
          />
          <ControllerFormNumberInput
            control={control}
            label={t('fields.cadRate')}
            helperText={t('exchangeRate.cadToUsd')}
            fieldName={CadRateField.fieldName}
            step={0.01}
          />
        </Stack>
        <Divider
          style={{
            marginTop: theme.spacing['8'],
            marginBottom: theme.spacing['6'],
          }}
        />
        <CloseConfirmModalFooter
          onClose={props.onClose}
          confirm={() => {}}
          isSubmitType={true}
          closeLabel={t('cancel')}
          confirmLabel={t('save')}
        />
      </form>
    </BaseModal>
  );
};
